// api/ 内部共通の fetch ラッパ・ApiError・認証トークン処理 (#1138)。
// ドメインロジックは置かない。api/ ディレクトリ内部専用 (index.ts から re-export しない)。
// Use relative path so Vite proxy handles forwarding to the API server
// This avoids CORS and mixed-content issues on phone access
import { onAuthStateChanged } from 'firebase/auth'
import { auth } from '../../lib/firebase'
import { getGuestId } from '../../lib/guestId'
import { reportApiFailure } from '../../lib/sentry'
import { notifyDevApiFailure } from '../../lib/devApiFailureNotice'
import i18n, { normalizeLocale } from '../../i18n/i18n'

export const API_BASE = '/api/v1'

/**
 * Generic API error — hides server internals from the user.
 *
 * `code` は backend が特定の失敗理由を区別させたい時に
 * `{"detail": {"code": "X", "message": "..."}}` で返す値。
 * 呼び出し側はこれを見て i18n キーを選ぶ (#392 decision limits 等)。
 */
export class ApiError extends Error {
  status: number
  code?: string
  // #1011: 429(ロック)時にサーバーが返す解除までの残り秒。UI が「あと○分」を表示する。
  retryAfterSeconds?: number
  /**
   * サーバが `{"detail": {...}}` で返した中身そのもの。
   *
   * code だけでは足りないケースのため (例: 売り切れで弾かれた時の
   * `soldOutItemIds` — どの行をカートから外せばよいか特定するのに要る)。
   * 追加のフィールドを返すたびに ApiError を作り替えずに済む。
   */
  detail?: Record<string, unknown>
  constructor(
    status: number,
    userMessage: string,
    code?: string,
    retryAfterSeconds?: number,
    detail?: Record<string, unknown>,
  ) {
    super(userMessage)
    this.status = status
    this.code = code
    this.retryAfterSeconds = retryAfterSeconds
    this.detail = detail
  }
}

/**
 * Firebase Auth の復元が終わるのを一度だけ待つ Promise。
 *
 * `auth.currentUser` は**復元が終わるまで null** で、その間に走った fetch は
 * 認証ヘッダ無しで飛んで 403 になる。ページの effect は認証ガードより先に走るので、
 * **リロード / URL 直開きのときだけ失敗する**という分かりにくい出方をしていた
 * （実際 Wallet の失効サマリは、この理由で local だと常に出ていなかった）。
 *
 * `onAuthStateChanged` の最初のコールバックは復元完了後に呼ばれるので、
 * それを待てば「未ログイン」と「復元中」を取り違えずに済む。
 * adminStampApi.ts が同じ問題を個別に回避していたが、原因はここなので大元で直す。
 */
let authRestored: Promise<void> | null = null
function waitForAuthRestore(): Promise<void> {
  if (!authRestored) {
    authRestored = new Promise<void>((resolve) => {
      // 復元が来なくても API を永久に止めない（ゲスト経路が死ぬため）
      const timer = setTimeout(() => { unsub(); resolve() }, 5_000)
      const unsub = onAuthStateChanged(auth, () => {
        clearTimeout(timer)
        unsub()
        resolve()
      })
    })
  }
  return authRestored
}

export async function getFirebaseIdToken(): Promise<string | null> {
  try {
    if (!auth.currentUser) await waitForAuthRestore()
    return (await auth.currentUser?.getIdToken()) ?? null
  } catch {
    return null
  }
}

// #995 スタッフモード: staff は Firebase ユーザーを持たず、PIN ログインで得た署名済み
// セッショントークンで認証する。設定されている間 apiFetch はこれを優先添付する。
// useStaffAuth が login/logout で設定/解除する。
export let _staffSessionToken: string | null = null
export function setStaffSessionToken(token: string | null): void {
  _staffSessionToken = token
}

/**
 * #1513 スタッフトークンを付けるのは**店舗側の画面にいる間だけ**。
 *
 * 以前は設定されている間すべての API 呼び出しに付けていたため、同じ端末で
 * スタッフ PIN ログインした後にユーザーアプリへ戻ると、顧客向け endpoint
 * (予約一覧 /reservations/me・予約作成・キャンセル) が Firebase トークン必須で
 * 403/401 になり、**画面には何も出ずに予約できない**状態になっていた
 * (demo 2026-08-22 のログ: POST /reservations 403 ×30、GET /reservations/me 403 が 15 秒おき)。
 * 店舗側 (/store/*, /staff/*) では従来どおり staff を優先する (同一端末をスタッフが
 * 交代で使う運用のため)。
 */
export function isStoreRoute(pathname: string): boolean {
  return /^\/(store|staff)(\/|$)/.test(pathname)
}

function staffTokenForCurrentRoute(): string | null {
  if (!_staffSessionToken) return null
  if (typeof window === 'undefined') return _staffSessionToken
  return isStoreRoute(window.location.pathname) ? _staffSessionToken : null
}

/**
 * `fetch` と同じ使い勝手で、**失敗の通知だけ**を足したもの (#1685)。
 *
 * `apiFetch` を使えない呼び出し用。使えない理由は主に認証で、店舗オーナーの
 * `idToken` を引数で受け取って自前で載せる系 (`store-auth/*`) や、スタッフ
 * コード/PIN で通す公開口 (`store-staff/*`) は、apiFetch が自動で付ける
 * 顧客/スタッフのトークンとは別物になる。
 *
 * **なぜ必要か**: 通知 (console / 非本番の画面帯 / Sentry) を apiFetch の中に
 * 置いたため、生 fetch の 37 箇所だけ**サーバが 500 を返しても誰にも届かない**
 * 状態だった。index 漏れは画面上「0 件」に化けるので、通知が無いと demo を
 * 素通りする (#1624 の議論)。
 *
 * レスポンスはそのまま返す。**エラーの投げ方は呼び出し側のまま**変えない
 * (メッセージや code の扱いが呼び出しごとに違うため)。
 */
export async function trackedFetch(url: string, init?: RequestInit): Promise<Response> {
  // tag / ログ用に API_BASE 以降だけを取り出す
  const path = url.startsWith(API_BASE) ? url.slice(API_BASE.length) : url
  let res: Response
  try {
    res = await fetch(url, init)
  } catch (e) {
    console.error(`[trackedFetch] network failure ${path}:`, e)
    notifyDevApiFailure(path, undefined, e instanceof Error ? e.message : String(e))
    reportApiFailure(e, { path })
    throw e
  }
  if (res.status >= 500) {
    // 本文は呼び出し側が読むかもしれないので **clone して** 読む
    // backend は detail を 2 形式で返す (#392): 文字列と `{code, message}`。
    // apiFetch と同じく両方を読む — 片方だけだと 502 等で detail が
    // (no detail) に落ちて、通知はされるのに原因が分からない
    let detail = ''
    let code: string | undefined
    try {
      const body = await res.clone().json()
      if (typeof body?.detail === 'string') {
        detail = body.detail
      } else if (body?.detail && typeof body.detail === 'object') {
        detail = typeof body.detail.message === 'string' ? body.detail.message : ''
        code = typeof body.detail.code === 'string' ? body.detail.code : undefined
      }
    } catch { /* JSON でない 500 もある */ }
    const shown = [code, detail].filter(Boolean).join(' ')
    console.error(`[trackedFetch] ${res.status} ${path}: ${shown || '(no detail)'}`)
    notifyDevApiFailure(path, res.status, shown)
    reportApiFailure(new ApiError(res.status, shown || 'server error', code), { path, status: res.status })
  }
  return res
}


/**
 * Common fetch wrapper.
 * - Attaches Firebase ID token as Authorization: Bearer for endpoints that require auth
 * - Keeps the legacy ?user_id= query param for endpoints not yet migrated (tracked in #54)
 * - Converts server errors to generic user-facing messages
 */
/** #1857 客がアプリで選んでいる言語。サーバは注文時にこの言語の品目名を焼き込む。
 *
 * **注文の作成経路は 6 つあり body に足すと漏れる**ので、X-Guest-Id と同じく
 * ヘッダで 1 か所から送る。i18next の現在値を見る (localStorage は未検出時に空)。 */
function currentLocale(): string {
  // #2156 **`zh-TW` を `zh` に潰さない。** 以前はここで頭を落としており、繁体字を
  // 選んだお客様の注文に簡体字の品目名が焼き込まれていた。
  return normalizeLocale(i18n.language)
}

export async function apiFetch(
  path: string,
  options: RequestInit = {},
  userId?: string,
  /**
   * #1922 このトークンで認証する (既定アプリのログインを使わない)。
   *
   * ゲストの購読準備 (#1641) は**購読専用アプリの匿名トークン**で
   * `/auth/realtime-claim` を呼ぶ。headers に載せても下で上書きされるので、
   * 「既定のログインを使わない」意図を引数で示す。
   *
   * **`string | null` にしない。** `null` を渡せる型だと、トークンを取り損ねた
   * 呼び出し側がそのまま渡したときに既定のログインへ黙って落ち、#1922 が再発する
   * (別人として認証される)。渡すなら必ず値があること。
   */
  authToken?: string,
): Promise<Response> {
  const headers: Record<string, string> = {
    'X-Yokoso-Locale': currentLocale(),
    ...(options.headers as Record<string, string> || {}),
  }

  // #995 スタッフセッションがあれば staff モードとして優先（同一端末をスタッフが交代で
  // 使う運用のため）。owner に戻るには staff ログアウト or /store/login（どちらも
  // staff セッションをクリアする）。
  // #1513 ただし店舗側の画面にいる間だけ。顧客画面では Firebase (顧客) トークンを使う
  // #1922 authToken が渡されたときは**それを使う** (既定アプリのトークンで上書き
  // しない)。上書きしていた頃は、店舗オーナーでログイン中の端末で客画面を開くと
  // ゲスト購読の customerId がオーナーのメールから導出された値になり、端末の
  // ゲスト ID と食い違って購読が始まらず、黙って 10 秒ポーリングに落ちていた。
  const idToken = authToken ?? staffTokenForCurrentRoute() ?? await getFirebaseIdToken()
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`
  }
  // #1398 未ログインならゲストIDを添える。サーバはゲストで通す口を卓注文まわりに
  // 限定しているので、他の endpoint は従来どおり 401 になる。
  // **発行済みの時だけ送る** — 単に閲覧しているだけの客に ID を配らない。
  // #1922 匿名トークン (authToken) のときも添える。そのトークンにはメールが無く、
  // サーバは誰の注文かをゲスト ID でしか決められないため。
  if (!idToken || authToken) {
    const guestId = getGuestId()
    if (guestId) headers['X-Guest-Id'] = guestId
  }

  // Legacy fallback for endpoints not yet secured by verify_customer_token (#54).
  const separator = path.includes('?') ? '&' : '?'
  const url = userId
    ? `${API_BASE}${path}${separator}user_id=${userId}`
    : `${API_BASE}${path}`

  // credentials: 'omit' — demo は Basic 認証 (#1251) が掛かっており、ブラウザは
  // 同一オリジンである /api/v1/ にも **キャッシュした `Authorization: Basic ...` を
  // 自動で付ける**。上で Bearer を載せられなかった時 (未ログイン / トークン復元失敗)
  // はそれがそのまま backend に届き、FastAPI の HTTPBearer が scheme 不一致で
  // 弾く — 「未ログイン」ではなく「権限がありません」に化けて、原因が追えなく
  // なっていた (予約が 403 になる件)。
  // #1807 の fastapi 0.109 → 0.141 で、ヘッダ欠落も scheme 不一致も
  // 401 "Not authenticated" に揃った (403 "Invalid authentication credentials"
  // ではなくなった) ので化けはしないが、**Basic を送る理由は無い**ので omit は残す。
  // API は cookie を一切使わない (Bearer のみ) ので omit で失うものは無い。
  let res: Response
  try {
    res = await fetch(url, { ...options, headers, credentials: 'omit' })
  } catch (e) {
    // 通信断 / DNS / CORS。5xx と同じく画面側で握られて消えるので、
    // コンソールにも Sentry にも残す
    console.error(`[apiFetch] network failure ${path}:`, e)
    notifyDevApiFailure(path, undefined, e instanceof Error ? e.message : String(e))
    reportApiFailure(e, { path })
    throw e
  }

  if (!res.ok) {
    // Try to extract detail from response for developer diagnostics only — never
    // surface raw backend detail (which is English) to the localized UI.
    //
    // backend は 2 種類の detail 形式を返す:
    //   1. 文字列: `{"detail": "Amount must be positive"}`
    //   2. オブジェクト: `{"detail": {"code": "PAYMENT_LIMIT_TX", "message": "..."}}`
    //      (#392 で導入。呼び出し側が code で i18n キーを選べるように)
    let detail = ''
    let code: string | undefined
    let detailObj: Record<string, unknown> | undefined
    try {
      const body = await res.json()
      if (typeof body?.detail === 'string') {
        detail = body.detail
      } else if (body?.detail && typeof body.detail === 'object') {
        detail = typeof body.detail.message === 'string' ? body.detail.message : ''
        code = typeof body.detail.code === 'string' ? body.detail.code : undefined
        detailObj = body.detail as Record<string, unknown>
      }
    } catch { /* ignore parse errors */ }
    // **5xx は detail の有無に関わらず必ず出す。** 以前は detail か code が
    // 取れた時だけ warn していたので、本文が JSON でない 500 (proxy のエラー
    // ページ等) では**コンソールにも何も残らなかった**。画面側は .catch() で
    // 握るため、ここで出さないと開発中に誰も気付けない (#1624 の議論)。
    // local / demo は Sentry の DSN 未設定 = no-op なので、コンソールが唯一の経路。
    if (res.status >= 500) {
      console.error(`[apiFetch] ${res.status} ${path}: ${code ?? ''} ${detail || '(no detail)'}`)
      // **非本番は画面にも出す。** demo は方針として Sentry の DSN を登録して
      // おらず、local も未設定。コンソールを開いていなければ誰も気付けないまま
      // demo を通過してしまうため、手動確認中に目に入るようにする (#1624 の議論)
      notifyDevApiFailure(path, res.status, [code, detail].filter(Boolean).join(' '))
    } else if (detail || code) {
      console.warn(`[apiFetch] ${res.status} ${path}: ${code ?? ''} ${detail}`)
    }

    // Generic localized error messages for users
    if (res.status === 401) throw new ApiError(res.status, '認証エラーが発生しました。再ログインしてください。', code, undefined, detailObj)
    if (res.status === 403) throw new ApiError(res.status, 'アクセス権限がありません。', code, undefined, detailObj)
    if (res.status === 404) throw new ApiError(res.status, 'リソースが見つかりません。', code, undefined, detailObj)
    if (res.status === 422) throw new ApiError(res.status, '入力内容に問題があります。', code, undefined, detailObj)
    // 429 is the Firestore / AI quota gate (#296 / #52). Caller decides
    // whether to show a toast or a full "blocked" screen — here we just
    // need a clear message so uncaught surfaces are at least informative.
    if (res.status === 429) throw new ApiError(res.status, '今月の閲覧枠を使い切りました。管理者へ連絡するか、翌月リセットをお待ちください。', code, undefined, detailObj)
    if (res.status >= 500) {
      // **5xx はここで Sentry に送る。** 画面側は .catch() で握って
      // 「読み込めませんでした」を出すが、握った時点で未捕捉例外ではなくなり
      // Sentry には乗らない。index 漏れ (FAILED_PRECONDITION) のような
      // 決定論的な失敗は再読み込みで直らないので、我々が気付けないと永久に
      // 直らない (#1624 の議論)。4xx は業務上の想定内なので送らない。
      const e = new ApiError(res.status, 'サーバーで問題が発生しています。しばらくしてからお試しください。', code, undefined, detailObj)
      reportApiFailure(e, { path, status: res.status })
      throw e
    }
    throw new ApiError(res.status, '処理に失敗しました。しばらくしてからもう一度お試しください。', code, undefined, detailObj)
  }

  return res
}
